/**
 * What the linter read from a host's files, kept per provider under each file's stamp, so a host that lints on each
 * edit reads an unchanged file once. Only the answer is kept, never the bytes. A host drops its provider when its
 * project view changes, and the answers go with it.
 */
/**
 * The stamp of `path`, or null where the provider gives none or its stamp read rejects: both mean the file is read.
 * A synchronous throw is a provider bug, and it propagates.
 */
export function stampOf(provider, path) {
    return provider.stamp ? provider.stamp(path).catch(() => null) : Promise.resolve(null);
}
/**
 * The content of `path`, or null for a file the provider does not hold. A rejection counts as a miss, since a provider
 * may fail a read rather than answer null. A synchronous throw propagates.
 */
export function loadOrNull(provider, path, type) {
    return provider.loadResource(path, type).catch(() => null);
}
export class StampedReads {
    /**
     * Each provider's reads. `get` writes a kept answer after each stamped read and a running read while it runs, and
     * deletes the running one once it settles. Both go with the provider.
     */
    byProvider = new WeakMap();
    /**
     * The answer `read` gives for `path`, kept under the stamp taken before the read while that stamp is unchanged. A null
     * answer, a file the read could not deliver, is never kept. A get shares a read of `path` still running under the
     * same stamp, so concurrent lints read an unchanged file once. With no stamp at all, the read starts at once.
     *
     * @param stamp - The file's stamp, when the caller started reading it earlier.
     */
    get(provider, path, read, stamp) {
        if (!stamp && !provider.stamp)
            return this.shared(provider, path, null, read);
        return this.stampedRead(provider, path, read, stamp ?? stampOf(provider, path));
    }
    /** The answer kept for `path`, whatever the file's stamp is now, or undefined for none. A running read is none. */
    peek(provider, path) {
        return this.byProvider.get(provider)?.kept.get(path)?.value;
    }
    async stampedRead(provider, path, read, stamp) {
        const current = await stamp;
        const before = this.readsOf(provider).kept.get(path);
        if (current !== null && before?.stamp === current)
            return before.value;
        return this.shared(provider, path, current, () => this.keptRead(provider, path, read, current));
    }
    /** The answer `read` gives, kept under `stamp` unless the stamp or the answer is null. */
    async keptRead(provider, path, read, stamp) {
        const value = await read();
        const { kept } = this.readsOf(provider);
        if (stamp === null || value === null)
            kept.delete(path);
        else
            kept.set(path, { stamp, value });
        return value;
    }
    /**
     * The read of `path` running under `stamp`, or a new one that `read` starts. A read under another stamp is not shared:
     * it may answer for content the file no longer has.
     */
    shared(provider, path, stamp, read) {
        const { running } = this.readsOf(provider);
        const inFlight = running.get(path);
        if (inFlight !== undefined && inFlight.stamp === stamp)
            return inFlight.answer;
        // Through an async function, so a read that throws at once rejects like one that fails later.
        const entry = { stamp, answer: (async () => read())() };
        running.set(path, entry);
        const settle = () => {
            if (running.get(path) === entry)
                running.delete(path);
        };
        entry.answer.then(settle, settle);
        return entry.answer;
    }
    readsOf(provider) {
        let reads = this.byProvider.get(provider);
        if (!reads) {
            reads = { kept: new Map(), running: new Map() };
            this.byProvider.set(provider, reads);
        }
        return reads;
    }
}
//# sourceMappingURL=stampedReads.js.map